import barba from "@barba/core";
import gsap from "gsap";
import Preloader from "./components/Preloader";
import CustomEase from "gsap/src/CustomEase";
import App from "./main";

gsap.registerPlugin(CustomEase);
import Menu from "./components/Menu";
import ProjectSingle from "./pages/ProjectSingle";
import projectCardHover from "./utils/projectCardHover";

export default class {
  constructor() {
    CustomEase.create(
      "figmaSpring",
      "M0,0 C0.05,0.2 0.1,0.8 0.2,1 0.35,1.15 0.5,0.95 0.65,1.02 0.8,1.05 0.9,0.98 1,1",
    );
    this.pages = window.pageConfig;

    this.orderedTabs = Object.entries(this.pages)
      .filter(([pageName]) => pageName !== "home")
      .sort((a, b) => a[1].index - b[1].index);
    if (window.innerWidth > 600) {
      this.desktopTransition();
      this.preserveActiveState();
    } else {
      this.mobileTransition();
      this.mobileFooterColor();
    }
    this.menu = new Menu();
    this.projectSingle = null;
  }

  // ============================================
  // FOOTER UPDATE METHODS
  // ============================================

  /**
   * Update footer based on current page type
   * @param {boolean} isProjectPage - Whether current page is a single project
   */
  async updateFooter(isProjectPage) {
    const footerWrapper = document.getElementById("footer-post-wrapper");
    if (!footerWrapper) return;

    const ajaxUrl = window.footerAjax?.ajaxUrl || footerWrapper.dataset.ajaxUrl;
    if (!ajaxUrl) {
      console.error("AJAX URL not found");
      return;
    }

    // Determine which action to call
    const action = isProjectPage ? "get_project_footer" : "get_random_footer";

    // Fade out
    footerWrapper.style.transition = "opacity 0.3s ease";
    footerWrapper.style.opacity = "0";

    // Build URL with cache buster
    const url = new URL(ajaxUrl);
    url.searchParams.set("action", action);
    url.searchParams.set("_cache", Date.now());

    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Network response was not ok");

      const html = await res.text();
      if (!html.trim()) throw new Error("Empty response");

      await new Promise((resolve) => setTimeout(resolve, 300));

      footerWrapper.innerHTML = html;

      // Update data attribute for next transition
      footerWrapper.dataset.isProject = isProjectPage ? "1" : "0";

      // Fade in
      footerWrapper.style.opacity = "1";

      // Re-init hover effects
      // this.initFooterRollovers();
      projectCardHover();
    } catch (err) {
      console.error("Footer update failed:", err);
      footerWrapper.style.opacity = "1";
    }
  }

  /**
   * Check if current page is a single project page
   */
  isProjectPage() {
    const body = document.body;
    if (
      body.classList.contains("single-project") ||
      body.classList.contains("post-type-project")
    ) {
      return true;
    }

    const footerWrapper = document.getElementById("footer-post-wrapper");
    if (footerWrapper && footerWrapper.dataset.isProject === "1") {
      return true;
    }

    return false;
  }

  /**
   * Initialize footer image rollovers
   */
  initFooterRollovers() {
    const wrapper = document.querySelector(".image_changer_wrapper");
    if (!wrapper) return;

    const images = wrapper.querySelectorAll(".image_rollover");
    if (images.length === 0) return;

    // Show first image by default
    images.forEach((img, index) => {
      img.style.position = index === 0 ? "relative" : "absolute";
      img.style.top = "0";
      img.style.left = "0";
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "cover";
      img.style.opacity = index === 0 ? "1" : "0";
      img.style.transition = "opacity 0.4s ease";
    });

    // Only add hover if there are multiple images
    if (images.length <= 1) return;

    wrapper.addEventListener("mouseenter", () => {
      images.forEach((img, i) => {
        img.style.opacity = i === 1 ? "1" : "0";
      });
    });

    wrapper.addEventListener("mouseleave", () => {
      images.forEach((img, i) => {
        img.style.opacity = i === 0 ? "1" : "0";
      });
    });
  }

  // ============================================
  // EXISTING METHODS (unchanged)
  // ============================================

  async slideLeft(next, current) {
    const nextPage = next.container.getAttribute("data-barba-namespace");
    const currentPage = current.container.getAttribute("data-barba-namespace");
    const tab = document.querySelector(`[data-link=${nextPage}]`);
    const allTabs = document.querySelectorAll("[data-link]");

    let interTabs = 0;
    const nextIndex = this.pages[nextPage].index;

    // Count intermediate tabs
    for (const [pageName, page] of this.orderedTabs) {
      if (page.index < nextIndex && pageName !== "home") {
        interTabs += 1;
      }
    }

    const tabsOnLeft = interTabs + 1; // +1 for the target tab itself
    const lastIndex = Math.max(
      ...Object.values(this.pages).map((page) => page.index),
    );

    const tabsOnRight = lastIndex - nextIndex;
    const leftOffset = 33 * tabsOnLeft; // e.g., 99px for inspiration
    const rightOffset = 33 * tabsOnRight; // e.g., 0px for inspiration

    // Set initial state with FINAL padding
    gsap.set(next.container, {
      position: "fixed",
      top: 0,
      left: this.pages[nextPage].leftStart,
      pointerEvents: "none",
      zIndex: 100,
      width: "100%",
    });
    const tl = gsap.timeline({
      onStart: () => {
        if (tab) {
          tab.classList.add("on-left");
        }
      },
      onUpdate: () => {
        const timelineDuration = tl.progress() * 100;
        if (timelineDuration > 40) {
          allTabs.forEach((element) => {
            gsap.to(element, {
              backgroundColor: this.pages[nextPage].background,
              duration: 0.1,
              ease: "none",
            });
          });
        }
      },
    });

    for (const [pageName, page] of this.orderedTabs) {
      if (page.index < nextIndex && pageName !== "home") {
        const intermediateTab = document.querySelector(
          `[data-link=${pageName}]`,
        );
        if (intermediateTab) {
          tl.to(
            intermediateTab,
            {
              left: page.leftEnd,
              duration: 1,
              ease: "power4.out",
              // onStart: () => {
              //   intermediateTab.style.backgroundColor =
              //     this.pages[nextPage].background;
              // },
            },
            0,
          );
        }
      }
    }

    tl.to(
      tab,
      {
        left: this.pages[nextPage].leftEnd,
        duration: 1,
        ease: "power4.out",
        onStart: () => {
          tab.style.backgroundColor = this.pages[nextPage].background;
        },
      },
      0,
    );

    tl.to(
      next.container,
      {
        left: `0`,
        duration: 1,
        ease: "power4.out",
        paddingLeft: `${leftOffset}px`,
        width: `calc(100% - ${(Object.keys(this.pages).length - 1) * 33}px)`,
      },
      0,
    );

    return tl.to(next.container, {
      position: "inherit",
      top: 0,
      left: 0,
      paddingLeft: `${leftOffset}px`,
      pointerEvents: "auto",
      zIndex: 100,
      onStart: () => {
        // FIX: Background colors are now animated above, so we don't snap them here
        // This prevents the color "flicker" during the slide
        gsap.set(current.container, { display: "none" });
        gsap.to(document.querySelector(".logo__header__wrapper"), {
          opacity: 1,
          duration: 0.1,
          ease: "none",
        });
      },
    });
  }

  async slideRight(next, current) {
    const nextPage = next.container.getAttribute("data-barba-namespace");
    const currentPage = current.container.getAttribute("data-barba-namespace");
    const tab = document.querySelector(`[data-link=${nextPage}]`);
    const allTabs = document.querySelectorAll("[data-link]");

    // FIX: Animate tab background colors at the start of the timeline instead of snapping
    const tl = gsap.timeline({
      onStart: () => {
        if (tab) {
          tab.classList.add("on-left");
        }
      },
      onUpdate: () => {
        const timelineDuration = tl.progress() * 100;
        if (timelineDuration > 40) {
          allTabs.forEach((element) => {
            gsap.to(element, {
              backgroundColor: this.pages[nextPage].background,
              duration: 0.1,
              ease: "none",
            });
          });
        }
      },
    });

    let interTabs = 0;
    const nextIndex = this.pages[nextPage].index;

    // Count intermediate tabs
    for (const [pageName, page] of this.orderedTabs) {
      if (page.index < nextIndex && pageName !== "home") {
        interTabs += 1;
      }
    }

    const tabsOnRight = nextIndex - 1;
    const rightOffset = 33 * tabsOnRight; // e.g., 0px for inspiration
    // Set initial state with FINAL padding
    gsap.set(next.container, {
      position: "fixed",
      top: 0,
      left: this.pages[nextPage].rightStart,
      pointerEvents: "none",
      paddingLeft: `${rightOffset}px`,
      zIndex: 100,
      width: "100%",
    });

    for (const [pageName, page] of this.orderedTabs) {
      if (page.index > nextIndex && pageName !== "home") {
        const intermediateTab = document.querySelector(
          `[data-link=${pageName}]`,
        );
        if (intermediateTab) {
          tl.to(
            intermediateTab,
            {
              left: page.rightEnd,
              duration: 1,
              ease: "power4.out",
              // onStart: () => {
              //   intermediateTab.style.backgroundColor =
              //     this.pages[nextPage].background;
              // },
            },
            0,
          );
        }
      }
    }
    tl.to(
      next.container,
      {
        left: `0`,
        duration: 1,
        ease: "power4.out",
        width: `calc(100% - ${(Object.keys(this.pages).length - 1) * 33}px)`,
      },
      0,
    );

    return tl.to(next.container, {
      position: "inherit",
      top: 0,
      left: 0,
      paddingLeft: `${rightOffset}px`,
      pointerEvents: "auto",
      zIndex: 100,
      onStart: () => {
        // FIX: Background colors are now animated above, so we don't snap them here
        gsap.set(current.container, { display: "none" });

        gsap.to(document.querySelector(".logo__header__wrapper"), {
          opacity: 1,
          duration: 0.1,
          ease: "none",
        });
      },
    });
  }

  async pageLeave(current) {
    gsap.set(current.container, {
      opacity: 1,
      position: "relative",
      top: 0,
      pointerEvents: "none",
      zIndex: 99,
      width: `calc(100% - ${(Object.keys(this.pages).length - 1) * 33}px)`,
    });
  }

  desktopTransition() {
    const transitions = {
      general: {
        name: "general-transition",
        leave: async ({ current }) => {
          await this.pageLeave(current);
        },
        afterLeave: ({ next, current }) => {
          const logoTimline = gsap.timeline({
            onComplete: () => {
              window.scrollTo({ top: 0, behavior: "instant" });
            },
          });
          const nextPage = next.container.getAttribute("data-barba-namespace");
          const currentPage = current.container.getAttribute(
            "data-barba-namespace",
          );
          logoTimline.to(document.querySelector(".logo__header__wrapper"), {
            duration: 0.3,
            opacity: 0,
            ease: "none",
          });
          logoTimline.to(document.querySelector(".logo__header__wrapper"), {
            left: this.pages[nextPage].logoPaddingLeft,
            duration: 0.1,
            ease: "power2.out",
          });
        },
        beforeEnter: ({ next }) => {
          const nextPage = next.container.getAttribute("data-barba-namespace");

          this.leaveDestroy();
        },
        enter: async ({ next, current }) => {
          const nextPage = next.container.getAttribute("data-barba-namespace");
          const currentPage = current.container.getAttribute(
            "data-barba-namespace",
          );

          const referencePage = this.pages[currentPage]
            ? currentPage
            : this.currentPage;

          const nextIndex = this.pages[nextPage].index;
          const currentIndex = this.pages[referencePage].index;

          this.currentPage = nextPage;

          gsap.set(document.querySelector("footer"), {
            paddingLeft: this.pages[nextPage].paddingLeft,
            paddingRight: this.pages[nextPage].paddingRight,
            background: this.pages[nextPage].background,
          });

          if (nextIndex > currentIndex) {
            await this.slideLeft(next, current);
          } else {
            await this.slideRight(next, current);
          }
        },
        afterEnter: ({ next }) => {
          this.afterEnter(next.container);
        },
      },
      projectPage: {
        name: "project-single-transition",
        to: {
          namespace: ["single_project"],
        },
        leave: ({ current }) => {
          return gsap.set(current.container, {
            opacity: 1,
            position: "fixed",
            top: 0,
            pointerEvents: "none",
            zIndex: 999999999,
            width: `calc(100% - ${(Object.keys(this.pages).length - 1) * 33}px)`,
          });
        },
        beforeEnter: () => {
          window.scrollTo({ top: 0, behavior: "instant" });
          this.leaveDestroy();
        },
        enter: ({ next, current }) => {
          const content = next.container;
          const nextPage = next.container.getAttribute("data-barba-namespace");
          const allTabs = document.querySelectorAll("[data-link]");
          if (allTabs) {
            allTabs.forEach((element) => {
              element.style.background = this.pages["project"].background;
            });
          }
          if (nextPage == "single_project") {
            gsap.set(content, {
              paddingLeft: this.pages["project"]?.paddingLeft,
              paddingRight: this.pages["project"]?.paddingRight,
              opacity: 1,
            });
            gsap.set(content, {
              paddingLeft: this.pages["project"]?.paddingLeft,
              paddingRight: this.pages["project"]?.paddingRight,
            });
            gsap.set(document.querySelector("footer"), {
              paddingLeft: this.pages["project"]?.paddingLeft,
              paddingRight: this.pages["project"]?.paddingRight,
              background: this.pages["project"].background,
            });

            gsap.set(document.querySelector(".logo__header__wrapper"), {
              left: this.pages["project"]?.logoPaddingLeft,
            });
            const currentTab = document.querySelector(
              `[data-link=${"project"}]`,
            );
            if (currentTab) {
              currentTab.classList.add("on-left");
              gsap.set(currentTab, {
                left: this.pages["project"].leftEnd,
              });
            }

            return gsap
              .timeline()
              .to(current.container, {
                opacity: 0,
                duration: 0.4,
                ease: "power2.out",
              })
              .to(next.container, {
                onComplete: () => {
                  gsap.set(current.container, {
                    display: "none",
                  });
                },
              });
          }
        },
        afterEnter: ({ next }) => {
          this.afterEnter(next.container);
        },
      },
    };
    barba.init({
      transitions: [transitions.general, transitions.projectPage],
      prevent: ({ event, href }) => {
        if (event.type === "click") {
          if (href === window.location.href) {
            event.preventDefault();
            event.stopPropagation();

            const currentPage = this.currentPage;
            const currentIndex = this.pages[currentPage].index;

            let prevPageName = null;
            for (const [pageName, page] of this.orderedTabs) {
              if (page.index === currentIndex - 1) {
                prevPageName = pageName;
                break;
              }
            }
            if (prevPageName == null) return;

            const prevLink = document.querySelector(
              `[data-link=${prevPageName}]`,
            );
            if (prevLink) {
              const prevHref = prevLink.querySelector("a").getAttribute("href");
              if (prevHref) {
                barba.go(prevHref);
              }
            } else {
              barba.go("/");
            }

            return true;
          }
        }
      },
      preventRunning: true,
    });
  }

  preserveActiveState() {
    const content = document.querySelector(".content");
    const currentPage = content.getAttribute("data-barba-namespace");
    const tab = document.querySelector(`[data-link=${currentPage}]`);

    const referencePage =
      currentPage === "single_project" ? "project" : currentPage;
    const nextIndex = this.pages[referencePage]?.index;

    this.currentPage = referencePage;

    for (const [pageName, page] of this.orderedTabs) {
      if (page.index < nextIndex && pageName !== "home") {
        const intermediateTab = document.querySelector(
          `[data-link=${pageName}]`,
        );
        if (intermediateTab) {
          gsap.set(intermediateTab, { left: page.leftEnd });
        }
      }
    }

    for (const [pageName, page] of this.orderedTabs) {
      if (page.index > nextIndex && pageName !== "home") {
        const intermediateTab = document.querySelector(
          `[data-link=${pageName}]`,
        );
        if (intermediateTab) {
          gsap.set(intermediateTab, { left: page.rightEnd });
        }
      }
    }

    const allTabs = document.querySelectorAll("[data-link]");
    allTabs.forEach((element) => {
      element.style.background = this.pages[referencePage]?.background;
    });

    gsap.set(content, {
      paddingLeft: this.pages[referencePage]?.paddingLeft,
      paddingRight: this.pages[referencePage]?.paddingRight,
    });
    gsap.set(document.querySelector("footer"), {
      paddingLeft: this.pages[referencePage]?.paddingLeft,
      paddingRight: this.pages[referencePage]?.paddingRight,
      background: this.pages[referencePage]?.background,
    });
    gsap.set(document.querySelector(".logo__header__wrapper"), {
      left: this.pages[referencePage]?.logoPaddingLeft,
    });

    const currentTab = document.querySelector(`[data-link=${referencePage}]`);
    if (currentTab) {
      currentTab.classList.add("on-left");
      gsap.set(currentTab, {
        left: this.pages[referencePage]?.leftEnd,
      });
    }
  }

  mobileTransition() {
    const transitions = {
      general: {
        name: "mobile-transition",
        leave: async ({ current }) => {},
        beforeEnter: () => {
          this.leaveDestroy();
        },
        enter: ({ next, current }) => {
          const nextPage = next.container.getAttribute("data-barba-namespace");
          const elementsMenu = document.querySelector(".menu-clicked");
          const direction = elementsMenu.getAttribute("data-direction");
          const tl = gsap.timeline({
            onComplete: () => {
              this.menu.closeMenu(0.5);
              this.mobileFooterColor();
            },
          });
          tl.to(elementsMenu, {
            background: this.pages[nextPage].background,
            duration: 0.1,
          });
          if (direction == "center") {
            tl.to(elementsMenu, {
              height: "100dvh",
              duration: 0.6,
              delay: 0.3,
              top: "0%",
              ease: "figmaSpring",
            });
            tl.to(
              document.querySelectorAll(
                ".single__menu__item__mobile:last-child",
              ),
              {
                top: "100%",
                ease: "figmaSpring",
                duration: 0.6,
              },
              0,
            );
          } else if (direction == "top") {
            tl.to(elementsMenu, {
              height: "100dvh",
              duration: 0.6,
              delay: 0.3,
              ease: "figmaSpring",
            });
            tl.to(
              document.querySelectorAll(
                ".single__menu__item__mobile:nth-child(2)",
              ),
              {
                top: "100%",
                ease: "figmaSpring",
                duration: 0.6,
              },
              0,
            );
            tl.to(
              document.querySelectorAll(
                ".single__menu__item__mobile:last-child",
              ),
              {
                top: "100%",
                ease: "figmaSpring",
                duration: 0.6,
              },
              0,
            );
          } else {
            tl.to(elementsMenu, {
              height: "100dvh",
              duration: 0.6,
              delay: 0.3,
              top: "0%",
              ease: "figmaSpring",
            });
            tl.to(
              [
                document.querySelectorAll(
                  ".single__menu__item__mobile:nth-child(2)",
                ),
                document.querySelectorAll(
                  ".single__menu__item__mobile:first-child",
                ),
              ],
              {
                duration: 0.6,
                ease: "power2.out",
                opacity: 0,
              },
              0,
            );
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
        afterEnter: ({ next }) => {
          this.afterEnter(next.container);
        },
      },
      projectPage: {
        name: "project-single-transition",
        to: {
          namespace: ["single_project"],
        },
        leave: ({ current }) => {
          return gsap.set(current.container, {
            opacity: 1,
            position: "fixed",
            top: 0,
            pointerEvents: "none",
            zIndex: 999999999,
            width: "100%",
          });
        },
        beforeEnter: () => {
          this.leaveDestroy();
        },
        enter: ({ next, current }) => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          const content = next.container;
          const nextPage = next.container.getAttribute("data-barba-namespace");
          if (nextPage == "single_project") {
            gsap.set(content, {
              opacity: 1,
            });
            return gsap
              .timeline()
              .to(current.container, {
                opacity: 0,
                duration: 0.4,
                ease: "power2.out",
              })
              .to(next.container, {
                onComplete: () => {
                  gsap.set(current.container, {
                    display: "none",
                  });
                },
              });
          }
        },
        afterEnter: ({ next }) => {
          this.afterEnter(next.container);
        },
      },
    };

    barba.init({
      transitions: [transitions.general, transitions.projectPage],
      preventRunning: true,
      prevent: ({ event, href }) => {
        if (event.type === "click") {
          event.preventDefault();
          event.stopPropagation();
          if (href === window.location.href) {
            this.menu.closeMenu(0.2);
            return;
          }
          document
            .querySelectorAll(".single__menu__item__mobile")
            .forEach((element) => {
              element.classList.remove("menu-clicked");
              gsap.to(element.querySelector("span"), {
                opacity: 0,
                duration: 0.2,
                ease: "none",
              });
              if (
                event.target.classList.contains("single__menu__item__mobile")
              ) {
                event.target.classList.add("menu-clicked");
              } else {
                this.menu.closeMenu(0.2);
              }
            });
          barba.go(href);
          return true;
        }
      },
    });
  }

  mobileFooterColor() {
    const content = document.querySelector(".content");
    const currentPage = content.getAttribute("data-barba-namespace");

    gsap.set(document.querySelector("footer"), {
      background: this.pages[currentPage]?.background,
    });
    if (currentPage == "single_project") {
      gsap.set(document.querySelector("footer"), {
        background: this.pages["project"]?.background,
      });
    }
  }

  afterEnter(container) {
    this.app = new App();
    this.app.createProjectPage();
    this.app.footerLogoFull();
    this.app.createProjectSinglePage(container);
    this.app.createInspirationPage();
    this.app.createStudioPage();
    this.app.createUtils();
    this.app.createHomePage();

    const nextPage = container.getAttribute("data-barba-namespace");
    const isProjectPage = nextPage === "single_project";

    this.updateFooter(isProjectPage);
  }

  leaveDestroy() {
    if (window.projectSingleInstance) {
      window.projectSingleInstance.destroy();
      window.projectSingleInstance = null;
    }
  }
}
